import { useT } from '../lib/i18n';
import { useEffect, useState, type FormEvent } from 'react';
import type { BdrBrandDto } from '@bingo/shared';
import { api, ApiError } from '../lib/api';

type Kind = 'logo' | 'stamp';

/** Master only: the company block, logo and stamp printed on every BDR (BINGO TOUR SASU). */
export function BdrSettings() {
  const [brands, setBrands] = useState<BdrBrandDto[] | null>(null);
  const [error, setError] = useState('');
  const [stamp, setStamp] = useState(0);
  const t = useT();

  const load = () =>
    api<{ brands: BdrBrandDto[] }>('/api/owner/bdr-settings')
      .then((r) => setBrands(r.brands))
      .catch((e) => setError(e instanceof ApiError ? e.message : t('Could not load the settings.')));
  useEffect(() => void load(), []);

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>{t('BDR settings')}</h1>
          <p className="muted">{t('Company block, logo and stamp printed on every BDR: BINGO TOUR SASU runs all the vans, for both brands.')}</p>
        </div>
      </div>
      {error && <div className="alert alert-bad">{error}</div>}
      {!brands ? <div className="muted">{t('Loading…')}</div> : brands.map((b) => (
        <BrandCard key={`${b.brandId}-${b.version}-${stamp}`} brand={b} onSaved={() => { setStamp((n) => n + 1); void load(); }} />
      ))}
    </div>
  );
}

function BrandCard({ brand, onSaved }: { brand: BdrBrandDto; onSaved: () => void }) {
  const [v, setV] = useState({
    companyName: brand.companyName ?? '', address: brand.address ?? '', phone: brand.phone ?? '', legalLine: brand.legalLine ?? '',
  });
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const t = useT();
  const set = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement>) => setV({ ...v, [k]: e.target.value });

  async function save(e: FormEvent) {
    e.preventDefault();
    setErr('');
    try {
      await api(`/api/owner/bdr-settings/${brand.brandId}`, {
        method: 'PUT',
        body: { companyName: v.companyName, address: v.address || null, phone: v.phone || null, legalLine: v.legalLine || null, version: brand.version },
      });
      setMsg(t('Saved.'));
      onSaved();
    } catch (e2) {
      setErr((e2 as Error).message);
    }
  }

  async function upload(kind: Kind, file: File | undefined) {
    if (!file) return;
    setErr('');
    if (file.size > 2 * 1024 * 1024) return setErr(t('The image is larger than 2 MB.'));
    const res = await fetch(`/api/owner/bdr-settings/${brand.brandId}/${kind}`, {
      method: 'PUT', credentials: 'same-origin', headers: { 'content-type': file.type || 'application/octet-stream' }, body: file,
    });
    if (!res.ok) return setErr((await res.json().catch(() => null))?.message ?? t('The image was not saved.'));
    onSaved();
  }

  async function remove(kind: Kind) {
    await api(`/api/owner/bdr-settings/${brand.brandId}/${kind}`, { method: 'DELETE' }).catch((e) => setErr((e as Error).message));
    onSaved();
  }

  const image = (kind: Kind, has: boolean, label: string) => (
    <div className="field">
      <span className="field-label">{t(label)}</span>
      {has ? (
        <img className="bdr-img" src={`/api/owner/bdr-settings/${brand.brandId}/${kind}?v=${brand.version}`} alt={`${t(label)} · ${brand.name}`} />
      ) : <span className="muted">{t('None')}</span>}
      <div className="bdr-actions">
        <label className="btn btn-sm">
          {has ? t('Replace (PNG or JPEG)') : t('Add (PNG or JPEG)')}
          <input type="file" accept="image/png,image/jpeg" hidden disabled={!brand.version} onChange={(e) => void upload(kind, e.target.files?.[0])} />
        </label>
        {has && <button type="button" className="btn btn-sm btn-ghost btn-danger" onClick={() => remove(kind)}>{t('Remove')}</button>}
      </div>
    </div>
  );

  return (
    <form className="card stack" style={{ padding: 16 }} onSubmit={save}>
      <h2>{brand.code} · {brand.name}</h2>
      {brand.code !== 'BT' && <p className="muted" style={{ margin: 0 }}>{t('For this brand only the logo is used: on the welcome signs of its clients. The BDR always carries the BINGO TOUR SASU block.')}</p>}
      <label className="field"><span>{t('Company name')}</span><input className="input" required value={v.companyName} onChange={set('companyName')} /></label>
      <label className="field"><span>{t('Address')}</span><input className="input" value={v.address} onChange={set('address')} /></label>
      <label className="field"><span>{t('Phone line')}</span><input className="input" value={v.phone} onChange={set('phone')} /></label>
      <label className="field"><span>{t('Legal line (SIRET, VAT)')}</span><input className="input" value={v.legalLine} onChange={set('legalLine')} /></label>
      <div className="bdr-actions">
        <button className="btn btn-primary">{t('Save')}</button>
        {msg && <span className="muted">{msg}</span>}
      </div>
      {!brand.version && <p className="muted">{t('Save the company name first, then add the logo and the stamp.')}</p>}
      <div className="bdr-imgs">
        {image('logo', brand.hasLogo, 'Logo')}
        {image('stamp', brand.hasStamp, 'Stamp and signature')}
      </div>
      {err && <div className="alert alert-bad">{err}</div>}
    </form>
  );
}
